import axios from 'axios';

// Centralized API configuration
const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_LINK || 'https://cx-staging-api.locobuzz.com/cmo-health',
  HEADERS: {
    'accept': 'application/json',
    'Content-Type': 'application/json'
  }
};

// Create axios instance
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: API_CONFIG.HEADERS,
  timeout: 30000 // 30 seconds timeout
});

// Helper for API requests
const apiRequest = async (endpoint, options = {}) => {
  try {
    const { method = 'GET', body, ...rest } = options;

    const config = {
      method,
      url: endpoint,
      ...rest
    };

    // If there's a body, set it as data for axios request
    if (body) {
      config.data = body;
    }

    const response = await apiClient(config);
    return response.data.data;
  } catch (error) {
    console.error(`Error in API request to ${endpoint}:`, error);
    throw error;
  }
};

// Sample data for mock methods
const sampleData = [
  {
    sector: 'Technology',
    brand: 'Apple',
    channels: {
      'Social Media': {
        accounts: ['@apple', '@appleSupport']
      },
      'News': {
        accounts: ['Apple Newsroom']
      }
    }
  },
  {
    sector: 'Finance',
    brand: 'Chase',
    channels: {
      'Social Media': {
        accounts: ['@Chase', '@ChaseSupport']
      }
    }
  }
];


// Simulate API delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Mock API calls using the existing sample data
export const apiService = {
  // Fetch all sectors data
  async fetchSectorsData() {
    await delay(800);
    return Promise.resolve(sampleData);
  },

  async fetchBrandChannels(sector, brand) {
    await delay(300);
    const brandData = sampleData.find(
      item => item.sector === sector && item.brand === brand
    );
    return Promise.resolve(brandData?.channels || {});
  },

  async fetchChannelAccounts(sector, brand, channel) {
    await delay(300);
    const brandData = sampleData.find(
      item => item.sector === sector && item.brand === brand
    );
    return Promise.resolve(brandData?.channels[channel]?.accounts || []);
  },

  async fetchDashboardStats() {
    return apiRequest('/data/analytics/mentions');
  },

  async fetchCategoryData(params = {}) {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.perPage || 20,
      category_id: params.categoryId || '',
      from_date: params.fromDate || '',
      to_date: params.toDate || '',
      ...params
    }).toString();

    const endpoint = `/data/analytics/categories${queryParams ? '?' + queryParams : ''}`;
    return apiRequest(endpoint);
  },

  async fetchCategoryList(params = {}) {
    const queryParams = new URLSearchParams({
      from_date: params.fromDate || '',
      to_date: params.toDate || '',
      ...params
    }).toString();

    const endpoint = `/data/analytics/category-list${queryParams ? '?' + queryParams : ''}`;
    return apiRequest(endpoint);
  },

  async fetchCategoryMentions() {
    return apiRequest('/data/analytics/category-mentions');
  },

  async fetchCategoryDetails(categoryId, categoryName) {
    return apiRequest('/data/analytics/category/details', {
      method: 'POST',
      body: {
        category_id: categoryId,
        category_name: categoryName
      }
    });
  },

  async fetchBrandDetails(categoryId, brandId, categoryName, brandName) {
    return apiRequest('/analytics/brand/details', {
      method: 'POST',
      body: {
        category_id: categoryId,
        brand_id: brandId,
        category_name: categoryName,
        brand_name: brandName
      }
    });
  },

  async fetchSearchResults(query, type = 'all', validationFilter = '', dateRange = {}) {
    try {
      const queryParams = new URLSearchParams({
        query: query || '',
        type: type || 'all'
      });

      // Add validation filter if it's provided
      if (validationFilter) {
        queryParams.append('validation_filter', validationFilter);
      }

      // Add date range parameters if provided
      if (dateRange.fromDate) {
        queryParams.append('from_date', dateRange.fromDate);
      }
      
      if (dateRange.toDate) {
        queryParams.append('to_date', dateRange.toDate);
      }

      const endpoint = `/analytics/search?${queryParams.toString()}`;
      return apiRequest(endpoint);
    } catch (error) {
      console.error('Error searching categories and brands:', error);
      throw error;
    }
  }
};